import {makeAutoObservable} from 'mobx'
import {DataObjectId} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MyStickersBoardSettingsTabInfo} from '@components/sticker/my-stickers/my-stickers'
import {CustomFieldsEditorBoardSettingsTabInfo} from '../crm/custom-fields-editor'

type AdditionalTabInfo = {
  [key: string]: unknown
} & MyStickersBoardSettingsTabInfo &
  CustomFieldsEditorBoardSettingsTabInfo

export type TabId =
  | 'boardSettings'
  | 'boardAppearance'
  | 'stickers'
  | 'boardSharing'
  | 'movementTemplates'
  | 'taskTemplates'
  | 'gantt'
  | 'separate-api'
  | 'customFields'

interface BoardSettingsState {
  isOpened: boolean
  currentTabId: TabId
  additionalTabInfo: AdditionalTabInfo
  allTabsDisabled: boolean
  disabledTabIds: TabId[]
  hasChanges?: boolean
  page?: BoardSettingsPage
  bgTabModified?: number
  boardId?: DataObjectId
}

export type BoardSettingsPage = 'create' | 'list'

export class BoardSettingsStore {
  state: BoardSettingsState = {
    isOpened: false,
    currentTabId: 'boardSettings',
    additionalTabInfo: {},
    allTabsDisabled: false,
    disabledTabIds: [],
    hasChanges: false,
    boardId: undefined
  }

  onHasChangesCb: (() => void) | undefined

  constructor() {
    makeAutoObservable(this, {}, {autoBind: true})
  }

  openForBoard(boardId: DataObjectId, tabId: TabId = 'boardSettings'): void {
    if (this.state.hasChanges) {
      this.onHasChangesCb?.()
      return
    }

    this.state.boardId = boardId
    this.state.currentTabId = tabId
    this.state.isOpened = true
    this.state.additionalTabInfo = {}
  }

  toggle(): void {
    if (this.state.hasChanges) {
      this.onHasChangesCb?.()
      return
    }

    if (this.state.isOpened) {
      this.close()
    } else {
      this.state.isOpened = true
    }
  }

  close(): void {
    this.state.isOpened = false
    this.state.additionalTabInfo = {}
    this.state.boardId = undefined
  }

  setCurrentTabId(tabId: TabId, page?: BoardSettingsPage): void {
    this.state.currentTabId = tabId
    this.state.page = page
    this.state.additionalTabInfo = {}

    if (tabId === 'movementTemplates') {
      void Tracking.send(
        TrackingEvent.boardSettingsOpenMovementTemplatesViaBoardSettings
      )
    }
  }

  setHasChanges(value: boolean): void {
    this.state.hasChanges = value
  }

  setOnHasChangesCb(value: (() => void) | undefined): void {
    this.onHasChangesCb = value
  }

  setAllTabsDisabled(value: boolean): void {
    this.state.allTabsDisabled = value
  }

  setDisabledTabs(disabledTabIds: TabId[]): void {
    this.state.disabledTabIds = disabledTabIds
  }

  addIntoAdditionalTabInfo(info: AdditionalTabInfo): void {
    this.state.additionalTabInfo = {
      ...this.state.additionalTabInfo,
      ...info
    }
  }

  removeFromAdditionalTabInfo(key: keyof AdditionalTabInfo): void {
    const {[key]: _, ...rest} = this.state.additionalTabInfo
    this.state.additionalTabInfo = rest
  }

  updateBgTabModified(): void {
    this.state.bgTabModified = Date.now()
  }
}

const boardSettingsStore = new BoardSettingsStore()
const boardSettingsState = boardSettingsStore.state
export {boardSettingsState, boardSettingsStore}
